import { Box, Button, Flex, Text, Tooltip } from "@powerledger/ui-component-lib";
import currency from "currency.js";
import { TFunction } from "i18next";
import { kebabCase, startCase, toLower } from "lodash";
import { Column, Row } from "react-table";
import ReactTooltip from "react-tooltip";

import { AttentionBadge } from "@/app/components/attention-badge";
import { ForwardTradeAttributesTooltip } from "@/app/components/forward-trade-attributes-tooltip";
import { ForwardTradeDetailsModalCustomProps } from "@/app/components/forward-trade-details-modal/forward-trade-details-modal.types";
import { ForwardTradeWithdrawCustomProps } from "@/app/components/forward-trade-withdraw-modal/forward-trade-withdraw-modal.types";
import { TableActionsButtons } from "@/app/components/table-actions-buttons";
import { TableValueWrapper } from "@/app/components/table-value-wrapper";
import { HeaderWithToolTip } from "@/app/components/tooltips";
import { UseAllFeatureToggleValues } from "@/app/hooks";
import { useActiveRegistryDisplayName } from "@/app/hooks/use-active-registry-display-name";
import { AppDateFormats, formatDate } from "@/app/lib/format-date";
import { getTotalForwardTradeValue } from "@/app/lib/forward-trade-helpers";
import { decodeAndParseHtmlContent } from "@/app/lib/html-encoder-decoder";
import { AssetAssignmentStatus, TradeContractStatus, TradePosition } from "@/app/types/generated/graphql";
import { AggregatedRegistryCode, TraceXSupportedRegistries } from "@/app/types/okta.types";

import { DownloadReportButtons, SupportedDocumentType } from "../components/download-report-buttons";
import { AssignRecsProps, TradeContractHistoryTableData } from "./forward-trade-history.types";

export const getForwardTradeColumns = (
  t: TFunction,
  actions: {
    handleAssignRecs: (args: AssignRecsProps) => void;
    handleWithdrawRecs: (args: ForwardTradeWithdrawCustomProps) => void;
    handleRequestDateChange: (tradeContract: TradeContractHistoryTableData) => void;
    showForwardTradeDetailsModal: (args: ForwardTradeDetailsModalCustomProps) => void;
    downloadTransactionSummary: (
      type: SupportedDocumentType,
      {
        orderId,
        orderNumber,
        rowRegistryCode,
      }: {
        orderId: string;
        orderNumber: string;
        rowRegistryCode: AggregatedRegistryCode;
      },
    ) => Promise<void>;
  },
  featureToggles?: Partial<ReturnType<UseAllFeatureToggleValues>["toggles"]>,
) => {
  const columns: Column<TradeContractHistoryTableData>[] = [
    {
      Header: t("Order Number"),
      accessor: "tradeContractNumber",
      disableSortBy: true,
    },
    {
      Header: <HeaderWithToolTip titleText="OLD" tooltipText="Order Lodgement Date" />,
      accessor: "createdDate",
      disableSortBy: false,
      Cell: ({ value }: { value: string }) => (
        <TableValueWrapper
          value={formatDate(value, { formatStyle: AppDateFormats.AbbreviatedMonthFormat, noTZ: true })}
        />
      ),
    },
    ...(featureToggles?.enableForwardSettlementDecoupling
      ? ([
          {
            Header: t("Payment Date"),
            accessor: (row: TradeContractHistoryTableData) => row.matchedTrade?.contractedPaymentDate,
            disableSortBy: false,
            Cell: ({ row }: { row: Row<TradeContractHistoryTableData> }) => {
              const { original } = row;
              const value =
                original.status === TradeContractStatus.Open
                  ? original.tradeSettlementDate
                  : original.matchedTrade?.contractedPaymentDate;
              return (
                <TableValueWrapper
                  value={formatDate(value, {
                    formatStyle: AppDateFormats.AbbreviatedMonthFormat,
                    noTZ: true,
                    noOffset: true,
                  })}
                />
              );
            },
          },
          {
            id: "contractedDeliveryDate",
            Header: (
              <HeaderWithToolTip
                titleText={t("Delivery Date (On or Before)")}
                tooltipText={t(
                  "Delivery will execute as soon as the seller assigns the full contracted volume that meets all requirements, any time on or before this date.",
                )}
              />
            ),
            accessor: (row: TradeContractHistoryTableData) => row.matchedTrade?.contractedDeliveryDate,
            disableSortBy: false,
            Cell: ({ row }: { row: Row<TradeContractHistoryTableData> }) => {
              const { original } = row;
              const value =
                original.status === TradeContractStatus.Open
                  ? original.tradeSettlementDate
                  : original.matchedTrade?.contractedDeliveryDate;
              return (
                <TableValueWrapper
                  value={formatDate(value, {
                    formatStyle: AppDateFormats.AbbreviatedMonthFormat,
                    noTZ: true,
                    noOffset: true,
                  })}
                />
              );
            },
          },
        ] as Column<TradeContractHistoryTableData>[])
      : [
          {
            Header: <HeaderWithToolTip titleText="TSD" tooltipText="Trade Settlement Date" />,
            accessor: "tradeSettlementDate",
            disableSortBy: false,
            Cell: ({ value }: { value: string }) => (
              <TableValueWrapper
                value={formatDate(value, {
                  formatStyle: AppDateFormats.AbbreviatedMonthFormat,
                  noTZ: true,
                  noOffset: true,
                })}
              />
            ),
          } as Column<TradeContractHistoryTableData>,
        ]),
    {
      Header: t("Status"),
      accessor: "status",
      disableSortBy: true,
      Cell: ({ value, row }: { value: string; row: Row<TradeContractHistoryTableData> }) => {
        const { original } = row;
        return (
          <Flex sx={{ color: "textDark", alignItems: "center", whiteSpace: "nowrap" }}>
            {`${t(startCase(toLower(value)))}`}
            {original.statusMessage && (
              <Tooltip
                id={`order-${original.id}-status-tooltip`}
                content={
                  <Box
                    sx={{
                      "& > p": {
                        marginY: 1,
                      },
                    }}
                  >
                    {decodeAndParseHtmlContent(t(original.statusMessage))}
                  </Box>
                }
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: 16,
                  height: 16,
                  marginTop: 1,
                }}
              />
            )}
          </Flex>
        );
      },
    },
    {
      Header: t("Quantity"),
      accessor: "volume",
      disableSortBy: true,
      Cell: ({ value }) => <TableValueWrapper value={value} />,
    },
    {
      Header: (
        <Box
          sx={{
            textAlign: "right",
            width: 100,
          }}
        >
          {t("Unit Price")}
        </Box>
      ),
      disableSortBy: true,
      accessor: "unitPrice",
      Cell: ({ value }: { value: string }) => {
        return (
          <Box
            sx={{
              textAlign: "right",
              color: "textDark",
              width: 100,
            }}
          >
            {currency(value || 0, { separator: ",", fromCents: true }).format()}
          </Box>
        );
      },
    },
    ...(featureToggles?.enableCrossRegistryForwardTrading
      ? ([
          {
            Header: () => t("Registry"),
            accessor: "registryCode",
            disableSortBy: true,
            Cell: ({ value }) => (
              <TableValueWrapper
                containerSx={{ minWidth: 60 }}
                value={
                  value
                    ? useActiveRegistryDisplayName(value as TraceXSupportedRegistries)?.activeRegistryDisplayName
                    : "Any"
                }
              />
            ),
          },
        ] as Column<TradeContractHistoryTableData>[])
      : []),
    {
      Header: () => t("Product Attributes"),
      id: "attributes",
      Cell: ({ row }: { row: Row<TradeContractHistoryTableData> }) => {
        const { original } = row;

        const tooltipRowData = {
          attributes: {
            commencementDate: original.attributes.commencementDate,
            fuelSources: original.attributes.fuelSources,
          },
          vintages: original.formattedAttributes.vintages,
          certificationsAndEligibilities: original.formattedAttributes.certificationsAndEligibilities,
          fuelSources: original.formattedAttributes.fuelSources,
          certificateTypes: original.formattedAttributes.certificateTypes,
        };

        const tooltipContent = (dataTip: string) => {
          return dataTip === "rec-tooltip" ? (
            <Box sx={{ color: "text", whiteSpace: "pre-line", backgroundColor: "shadow", p: 3, borderRadius: 8 }}>
              <ForwardTradeAttributesTooltip
                tooltipData={tooltipRowData}
                rowRegistryCode={original.registryCode as AggregatedRegistryCode}
              />
            </Box>
          ) : null;
        };
        const vintagesText = original.formattedAttributes.vintages?.[0];
        const eligibilitiesText = original.formattedAttributes.eligibilities?.length
          ? `, ${original.formattedAttributes.eligibilities}`
          : "...";

        return (
          <>
            <ReactTooltip
              id={original.id}
              backgroundColor="shadow"
              arrowColor="#D0D5DD"
              getContent={(dataTip) => tooltipContent(dataTip)}
              padding="0"
            />
            <Text
              sx={{
                display: "inline-block",
                width: "120px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                cursor: "pointer",
              }}
              data-tip="rec-tooltip"
              data-for={original.id}
            >
              {vintagesText}
              {eligibilitiesText}
            </Text>
          </>
        );
      },
    },
    {
      Header: () => t("Order Type"),
      accessor: "position",
      disableSortBy: true,
      Cell: ({ value }: { value: string }) => (
        <Box
          sx={{
            variant: value === TradePosition.Ask ? "chips.error" : "chips.info",
            width: "40px",
          }}
        >
          {value}
        </Box>
      ),
    },
    {
      Header: () => t("Total"),
      id: "totaltradevalue",
      disableSortBy: true,
      Cell: ({ row }: { row: Row<TradeContractHistoryTableData> }) => {
        const { original } = row;
        const tradeValue = getTotalForwardTradeValue(original.unitPrice, original.volume).value;
        return <Box>{currency(tradeValue || 0, { separator: ",", fromCents: true }).format()}</Box>;
      },
    },
    {
      Header: () => t("Actions"),
      id: "forwardActions",
      Cell: ({ row }: { row: Row<TradeContractHistoryTableData> }) => {
        const {
          status,
          position,
          tradeContractNumber,
          id,
          volume,
          attributes,
          hasActionableChangeRequest,
          assetAssignmentStatus,
          registryCode,
        } = row.original;

        const actionAssignRecs = () =>
          actions.handleAssignRecs({
            tradeContractNumber,
            tradeContractRegistryCode: row.original.registryCode as AggregatedRegistryCode,
            tradeContractId: id,
            askTradeVolume: volume,
            tradeAttributes: attributes,
            status,
            assetAssignmentStatus,
          });

        const isPositionAsk = position === TradePosition.Ask;

        const showAssignAssets =
          isPositionAsk &&
          assetAssignmentStatus === AssetAssignmentStatus.NotAssigned &&
          (status === TradeContractStatus.Contracted || status === TradeContractStatus.PendingComplete);

        const showViewAssets = isPositionAsk && assetAssignmentStatus === AssetAssignmentStatus.Assigned;

        const actionLists: {
          actionName: string;
          onAction: () => void | Promise<void>;
          showAction: boolean;
          showAttentionBadge?: boolean;
        }[] = [
          {
            actionName: "See Details",
            onAction: () => actions.showForwardTradeDetailsModal({ tradeContract: row.original }),
            showAction: true,
          },
          {
            actionName: "Withdraw",
            onAction: () =>
              actions.handleWithdrawRecs({
                tradeID: id,
                registryCode: registryCode as AggregatedRegistryCode,
              }),
            showAction: status === TradeContractStatus.Open,
          },
          {
            actionName: "Request Date Change",
            onAction: () => actions.handleRequestDateChange(row.original),
            showAttentionBadge: Boolean(hasActionableChangeRequest),
            showAction:
              featureToggles?.enableForwardSettlementDecoupling && TradeContractStatus.PendingReview === status,
          },
          {
            actionName: "Assign RECs",
            onAction: actionAssignRecs,
            showAction: showAssignAssets,
          },
          {
            actionName: "View RECs",
            onAction: actionAssignRecs,
            showAction: showViewAssets,
          },
          {
            actionName: "Download CSV",
            onAction: async () => {
              await actions.downloadTransactionSummary("csv", {
                orderId: row.original.id,
                orderNumber: row.original.tradeContractNumber,
                rowRegistryCode: row.original.registryCode as AggregatedRegistryCode,
              });
            },
            showAction: [TradeContractStatus.Completed, TradeContractStatus.Withdrawn].includes(status),
          },
          {
            actionName: "Download PDF",
            onAction: async () => {
              await actions.downloadTransactionSummary("pdf", {
                orderId: row.original.id,
                orderNumber: row.original.tradeContractNumber,
                rowRegistryCode: row.original.registryCode as AggregatedRegistryCode,
              });
            },
            showAction: [TradeContractStatus.Completed, TradeContractStatus.Withdrawn].includes(status),
          },
        ];

        const filteredActionLists = actionLists
          .filter((action) => action.showAction)
          .map(({ actionName, onAction, showAttentionBadge }) => {
            if (actionName.startsWith("Download")) {
              return (
                <DownloadReportButtons
                  key={actionName}
                  actionName={actionName}
                  onAction={onAction as () => Promise<void>}
                />
              );
            }

            return (
              <AttentionBadge key={actionName} showAttentionBadge={showAttentionBadge}>
                <Button
                  key={actionName}
                  variant="pill.compactSecondary"
                  sx={{
                    py: 0,
                    px: 3,
                    height: "30px",
                    boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
                  }}
                  onClick={onAction}
                  data-testid={kebabCase(actionName)}
                >
                  {t(actionName)}
                </Button>
              </AttentionBadge>
            );
          });

        return (
          <TableActionsButtons
            actions={filteredActionLists}
            showAttentionBadge={
              featureToggles?.enableForwardSettlementDecoupling && Boolean(hasActionableChangeRequest)
            }
          />
        );
      },
    },
  ];
  return columns;
};
